import ConversationApi from 'dashboard/api/inbox/conversation';
import { isAInboxViewRoute } from 'dashboard/helper/routeHelpers';
import types from 'dashboard/store/mutation-types';
import {
  beginConversationPositionRestore,
  clearConversationPosition,
  getConversationRouteId,
} from 'dashboard/store/modules/conversations/helpers/historyPosition';
import { clearConversationHistoryWindows } from 'dashboard/store/modules/conversations/helpers/historyRequests';
import {
  captureConversationAccess,
  conversationFramesReady,
  invalidateConversationAccess,
  invalidateConversationSession,
  restoreConversationAccess,
  revokeConversationAccess,
  startConversationAccessChange,
  suspendConversationFrames,
  resumeConversationFrames,
} from './conversationReads';

// Modules caching data of individual conversations. The server scopes
// notifications and contact history by list eligibility (ConversationAccess
// #scope); the others need direct access.
const listScopedCaches = ['contactConversations', 'notifications', 'kanban'];
const conversationCaches = [
  ...listScopedCaches,
  'conversationLabels',
  'conversationMetadata',
  'conversationWatchers',
  'conversationSearch',
  'conversationTypingStatus',
];

export const clearAccess = (
  context,
  {
    session = false,
    preserveReadingPosition = false,
    preserveComposer = false,
  } = {}
) => {
  const { rootState, commit, dispatch } = context;
  clearConversationHistoryWindows(rootState);
  if (session || !preserveReadingPosition) clearConversationPosition(rootState);
  if (session) {
    invalidateConversationSession(rootState);
    dispatch('agents/reset', undefined, { root: true });
  } else invalidateConversationAccess(rootState);
  commit(
    types.CLEAR_CONVERSATION_ACCESS,
    preserveComposer ? { preserveComposer: true } : undefined,
    { root: true }
  );
  [...conversationCaches, 'conversationStats'].forEach(module => {
    commit(`${module}/${types.CLEAR_CONVERSATION_ACCESS}`, undefined, {
      root: true,
    });
  });
  dispatch('permissionSets/management/reset', undefined, { root: true });
  dispatch('conversationUnreadCounts/clear', undefined, { root: true });
  dispatch('conversationPage/reset', undefined, { root: true });
};

// Reloads, merging, the server-scoped collections on screen: the first page of
// the current list (Mentions included), unread counts and notifications.
// Resolves false when `isCurrent` stops holding meanwhile.
export const reloadVisibleCollections = async (context, isCurrent) => {
  const { rootState, rootGetters, commit, dispatch } = context;
  // Restarting from page one reopens pagination that had reached its end.
  dispatch('conversationPage/reset', undefined, { root: true });
  const applied = rootGetters.getAppliedConversationFiltersQuery;
  const folder = rootGetters['customViews/getActiveConversationFolder'];
  const query = applied?.payload?.length ? applied : folder?.query;
  if (query) {
    await dispatch(
      'fetchFilteredConversations',
      { queryData: query, page: 1 },
      { root: true }
    );
  } else {
    await dispatch(
      'updateChatListFilters',
      { page: 1, updatedWithin: null },
      { root: true }
    );
    if (!isCurrent()) return false;
    await dispatch('fetchAllConversations', undefined, { root: true });
  }
  if (!isCurrent()) return false;
  await dispatch('conversationUnreadCounts/get', undefined, { root: true });
  if (!isCurrent()) return false;
  const routeName = rootState.route.name;
  if (['kanban_board', 'kanban_board_by_key'].includes(routeName)) {
    await dispatch('kanban/fetchBoard', undefined, { root: true });
    if (!isCurrent()) return false;
  }
  if (isAInboxViewRoute(routeName, true)) {
    commit(
      `notifications/${types.SET_NOTIFICATIONS_UI_FLAG}`,
      { isAllNotificationsLoaded: false },
      { root: true }
    );
    await dispatch(
      'notifications/index',
      { ...rootGetters['notifications/getNotificationFilters'], page: 1 },
      { root: true }
    );
    if (!isCurrent()) return false;
  }
  await dispatch('notifications/unReadCount', undefined, { root: true });
  if (!isCurrent()) return false;
  return true;
};

// Reloads and reselects the conversation of the route after an access change.
// Resolves false when `isCurrent` stops holding meanwhile.
const restoreRouteConversation = async (
  context,
  { route, selectedId, isCurrent }
) => {
  const { rootState, dispatch } = context;
  const detail = await dispatch('getConversation', selectedId, { root: true });
  if (!isCurrent()) return false;
  if (rootState.route !== route) return true;
  const conversation =
    detail &&
    rootState.conversations.allConversations.find(
      item => item.id === selectedId
    );
  if (conversation) {
    await dispatch('setActiveChat', { data: conversation }, { root: true });
    if (isCurrent() && rootState.route === route) {
      await dispatch(
        'markMessagesRead',
        { id: conversation.id },
        { root: true }
      );
    }
  } else {
    clearConversationPosition(rootState);
  }
  return true;
};

export const revalidateAccess = async context => {
  const { rootState, dispatch } = context;
  const route = rootState.route;
  const selectedId = getConversationRouteId(context);
  beginConversationPositionRestore(context);
  clearAccess(context, { preserveReadingPosition: true });
  suspendConversationFrames(rootState);
  const isCurrent = captureConversationAccess(context);
  // Coalesced: a read superseded by another profile read or write resolves with
  // the newer one, so frames never stay suspended behind it. Each device keeps
  // its UI settings (layout); access revalidation refreshes permissions only.
  const refreshed = await dispatch(
    'validityCheck',
    { preserveUISettings: true, coalesce: true },
    { root: true }
  );
  if (!refreshed || !isCurrent()) return null;
  resumeConversationFrames(rootState, isCurrent);

  if (!(await reloadVisibleCollections(context, isCurrent))) return null;
  if (!selectedId || rootState.route !== route) return isCurrent;
  return (await restoreRouteConversation(context, {
    route,
    selectedId,
    isCurrent,
  }))
    ? isCurrent
    : null;
};

// The participants panel of a conversation, read last: its failure keeps the
// previous list (the panel reads it again when opened) and stops nothing else.
const refreshParticipants = async ({ dispatch }, conversationId) => {
  try {
    await dispatch(
      'conversationWatchers/show',
      { conversationId },
      { root: true }
    );
  } catch (error) {
    // The previous participants stay until the panel reads them again.
  }
};

const pruneCaches = ({ commit }, modules, conversationId) => {
  modules.forEach(module => {
    commit(`${module}/${types.REVOKE_CONVERSATION_ACCESS}`, conversationId, {
      root: true,
    });
  });
};

// Direct access to one conversation was lost: what is cached for it goes, and
// its reads and frames composed before the loss are fenced off.
const revokeConversation = (context, conversationId) => {
  const { rootState, commit } = context;
  revokeConversationAccess(rootState, conversationId, 'show');
  if (getConversationRouteId(context) === conversationId)
    clearConversationPosition(rootState);
  commit(types.REVOKE_CONVERSATION_ACCESS, conversationId, { root: true });
  pruneCaches(context, conversationCaches, conversationId);
};

// List eligibility was lost while direct access remains (a transfer away by a
// participant): the conversation leaves the list-scoped collections and stays
// open if it is.
const unlistConversation = (context, conversationId) => {
  revokeConversationAccess(context.rootState, conversationId, 'list');
  context.commit(
    types.UPDATE_CONVERSATION_LIST_ACCESS,
    { conversationId, listAccess: false },
    { root: true }
  );
  pruneCaches(context, listScopedCaches, conversationId);
};

// Reads the conversation and applies the server's answer, which also settles
// lost and gained events of it delivered out of order. Resolves true when it
// is readable, false when denied, and null when unanswered or superseded by a
// newer access event of the same conversation.
const confirmConversationAccess = async (context, conversationId, isLatest) => {
  const { rootState, commit } = context;
  let conversation;
  try {
    ({ data: conversation } = await ConversationApi.show(conversationId));
  } catch (error) {
    if (!isLatest() || ![401, 403, 404].includes(error?.response?.status))
      return null;
    revokeConversation(context, conversationId);
    return false;
  }
  if (!isLatest()) return null;
  if (conversation.permission_set_list_access === false)
    unlistConversation(context, conversationId);
  else {
    restoreConversationAccess(rootState, conversationId);
    // The confirmed eligibility applies even when a newer frame already moved
    // the record past this snapshot and the timestamped merge below skips it.
    if (conversation.permission_set_list_access === true)
      commit(
        types.UPDATE_CONVERSATION_LIST_ACCESS,
        { conversationId, listAccess: true },
        { root: true }
      );
  }
  const known = rootState.conversations.allConversations.some(
    item => item.id === conversationId
  );
  if (known) {
    commit(types.UPDATE_CONVERSATION, conversation, { root: true });
    commit(`contacts/${types.SET_CONTACT_ITEM}`, conversation.meta.sender, {
      root: true,
    });
  }
  return true;
};

// Access to one conversation may have been lost. The access revision is kept,
// so nothing else is cleared or fenced. Once the server confirms it:
// - no direct access: the conversation is revoked;
// - direct access without list eligibility: it leaves the lists, and stays
//   open if it is;
// - still listed: the loss was superseded and the record is refreshed.
// Its participants reload, as the loss may be of a participation. Without an
// answer it fails closed for this conversation only. The server-scoped
// collections on screen are then merged again from page one, which rebases
// their pagination and supersedes older reads of them.
export const syncLostAccess = async (context, conversationId) => {
  const { rootState, dispatch } = context;
  // Frames left suspended by a failed revalidation need the full one first.
  if (!conversationFramesReady(rootState)) {
    await dispatch('revalidateAccess');
    return;
  }
  const isLatest = startConversationAccessChange(context, conversationId);
  const route = rootState.route;
  const readable = await confirmConversationAccess(
    context,
    conversationId,
    isLatest
  );
  if (!isLatest()) return;
  if (readable === null) revokeConversation(context, conversationId);
  const reloaded = await reloadVisibleCollections(context, isLatest);
  if (!reloaded) return;
  // Readable but not on screen (an earlier revocation, superseded): the route
  // conversation comes back, as on a gain.
  if (
    readable &&
    getConversationRouteId(context) === conversationId &&
    rootState.conversations.selectedChatId !== conversationId &&
    !(await restoreRouteConversation(context, {
      route,
      selectedId: conversationId,
      isCurrent: isLatest,
    }))
  )
    return;
  const known = rootState.conversations.allConversations.some(
    item => item.id === conversationId
  );
  if (known) await refreshParticipants(context, conversationId);
};

// Access to one conversation was granted. A gain keeps the access revision and
// cannot revoke anything, so nothing is cleared: the server-scoped collections
// on screen are merged again (adding the conversation wherever the server now
// lists it), the route conversation is restored if it is the gained one, and
// the visible contact history and the conversation's participants reload.
export const syncGainedAccess = async (context, conversationId) => {
  const { rootState, dispatch } = context;
  // Frames left suspended by a failed revalidation need the full one first.
  if (!conversationFramesReady(rootState)) {
    await dispatch('revalidateAccess');
    return;
  }
  // A gain event may be older than a loss delivered before it: the server
  // confirms the access before the conversation's fence is lifted.
  const isLatest = startConversationAccessChange(context, conversationId);
  const route = rootState.route;
  const readable = await confirmConversationAccess(
    context,
    conversationId,
    isLatest
  );
  // A newer access event of this conversation took over.
  if (!isLatest()) return;
  const reloaded = await reloadVisibleCollections(context, isLatest);
  // Denied: the gain was older than a loss, and the conversation is revoked.
  if (!reloaded || readable === false) return;
  if (getConversationRouteId(context) === conversationId) {
    const restored = await restoreRouteConversation(context, {
      route,
      selectedId: conversationId,
      isCurrent: isLatest,
    });
    if (!restored) return;
  }
  const contactId = rootState.conversations.allConversations.find(
    item => item.id === rootState.conversations.selectedChatId
  )?.meta?.sender?.id;
  if (contactId)
    await dispatch('contactConversations/get', contactId, { root: true });
  // A loss of this conversation delivered meanwhile left nothing to read.
  if (!isLatest()) return;
  await refreshParticipants(context, conversationId);
};
